import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/1042a9a6642851926811b24c6d85389b9e8953f00b3548af03ba75980a43dde9.jpeg";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private Val d'Orcia day tour with chauffeur",
  name: "Private Val d'Orcia Tour",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Val d'Orcia", "Pienza", "Montalcino", "Montepulciano", "Siena", "Tuscany"],
  description:
    "A private, chauffeur-driven day through the Val d'Orcia and southern Tuscany — Pienza, Montalcino, Montepulciano and the cypress-lined roads, in a Mercedes V-Class.",
  url: "https://yourblacklimo.com/val-orcia-private-tour",
};

const CONTENT = {
  en: {
    seoTitle: "Private Val d'Orcia Tour | Southern Tuscany Chauffeur Day Trip — Your Black Limo",
    seoDesc:
      "A private, chauffeur-driven day through the Val d'Orcia — Pienza, Montalcino, Montepulciano, thermal Bagno Vignoni and the iconic cypress roads. Bespoke pace in a Mercedes V-Class.",
    overline: "Val d'Orcia Tour",
    h1: "Private Val d'Orcia & Southern Tuscany Tour",
    intro:
      "The Tuscany of postcards and paintings — golden hills, lone cypress trees and hilltop villages steeped in wine. A UNESCO World Heritage landscape, explored slowly from the comfort of a private Mercedes V-Class.",
    heroAlt: "Cypress-lined golden-hour road winding through the Val d'Orcia in Tuscany",
    blocks: [
      {
        title: "The postcard landscape",
        body: "The Val d'Orcia is the image the world holds of Tuscany: rolling wheat fields, solitary farmhouses and the famous cypress avenues. We know exactly where and when the light is best, and we build in the photo stops that make the day unforgettable.",
      },
      {
        title: "Wine & villages",
        body: "This is the land of Brunello di Montalcino and Vino Nobile di Montepulciano, of Pienza's pecorino and Renaissance streets. The day unfolds at a pace that never feels rushed — any tastings and lunches along the way are booked and paid directly by the client.",
      },
    ],
    cards: {
      overline: "Highlights",
      title: "Across southern Tuscany",
      items: [
        { title: "Pienza", body: "The 'ideal Renaissance town' and home of Tuscany's finest pecorino cheese." },
        { title: "Montalcino", body: "Hilltop fortress town and cradle of the majestic Brunello di Montalcino." },
        { title: "Montepulciano", body: "Noble palazzi, deep cellars and the elegant Vino Nobile." },
        { title: "Bagno Vignoni", body: "A village built around a steaming Renaissance thermal pool." },
      ],
    },
    note: {
      title: "How we do it",
      body:
        "The Val d'Orcia lies south of Siena, about two hours from Lucca or Pisa — a full, leisurely day rewarded by the finest scenery in Tuscany. We stop wherever the view calls; any winery tastings and lunches are booked and paid directly by the client. Fully licensed NCC, professional English-speaking drivers, Mercedes V-Class for up to seven guests.",
    },
    cta: {
      title: "Plan your Val d'Orcia day",
      secondaryLabel: "Explore wine tours",
      secondaryHref: "/tuscany-wine-tours",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "How far is the Val d'Orcia from Lucca or Pisa?", a: "About two hours each way, which makes it a full and rewarding day — we plan the route so you spend the time enjoying the scenery, not the logistics." },
        { q: "Is this good for photography?", a: "Wonderful. We know the classic cypress roads and the best light, and build in the photo stops that make the Val d'Orcia so iconic." },
        { q: "Is the tour private?", a: "Entirely. It is a fully private day in your own Mercedes V-Class with a dedicated chauffeur." },
      ],
    },
  },
  it: {
    seoTitle: "Tour Privato della Val d'Orcia | Escursione con Autista nella Toscana del Sud — Your Black Limo",
    seoDesc:
      "Una giornata privata nella Val d'Orcia con autista — Pienza, Montalcino, Montepulciano, le terme di Bagno Vignoni e le iconiche strade dei cipressi. Ritmo su misura in Mercedes Classe V.",
    overline: "Tour della Val d'Orcia",
    h1: "Tour Privato della Val d'Orcia e della Toscana del Sud",
    intro:
      "La Toscana delle cartoline e dei dipinti — colline dorate, cipressi solitari e borghi collinari intrisi di vino. Un paesaggio Patrimonio dell'Umanità UNESCO, esplorato con calma dal comfort di una Mercedes Classe V privata.",
    heroAlt: "Strada fiancheggiata da cipressi all'ora dorata nella Val d'Orcia in Toscana",
    blocks: [
      {
        title: "Il paesaggio da cartolina",
        body: "La Val d'Orcia è l'immagine che il mondo ha della Toscana: campi di grano ondulati, casali solitari e i celebri viali di cipressi. Sappiamo esattamente dove e quando la luce è migliore, e inseriamo le soste fotografiche che rendono la giornata indimenticabile.",
      },
      {
        title: "Vino e borghi",
        body: "È la terra del Brunello di Montalcino e del Vino Nobile di Montepulciano, del pecorino di Pienza e delle sue vie rinascimentali. La giornata scorre a un ritmo che non ha mai fretta — eventuali degustazioni e pranzi lungo il percorso sono prenotati e pagati direttamente dal cliente.",
      },
    ],
    cards: {
      overline: "Da non perdere",
      title: "Nella Toscana del sud",
      items: [
        { title: "Pienza", body: "La 'città ideale del Rinascimento' e patria del miglior pecorino di Toscana." },
        { title: "Montalcino", body: "Borgo-fortezza in cima al colle e culla del maestoso Brunello di Montalcino." },
        { title: "Montepulciano", body: "Palazzi nobiliari, cantine profonde e l'elegante Vino Nobile." },
        { title: "Bagno Vignoni", body: "Un borgo costruito attorno a una fumante vasca termale rinascimentale." },
      ],
    },
    note: {
      title: "Come lavoriamo",
      body:
        "La Val d'Orcia si trova a sud di Siena, a circa due ore da Lucca o Pisa — una giornata intera e rilassata ripagata dai paesaggi più belli della Toscana. Ci fermiamo ovunque il panorama lo chieda; eventuali degustazioni e pranzi sono prenotati e pagati direttamente dal cliente. NCC regolarmente autorizzato, autisti professionisti che parlano inglese, Mercedes Classe V fino a sette ospiti.",
    },
    cta: {
      title: "Organizza la tua giornata in Val d'Orcia",
      secondaryLabel: "Esplora i tour del vino",
      secondaryHref: "/tuscany-wine-tours",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Quanto dista la Val d'Orcia da Lucca o Pisa?", a: "Circa due ore per tratta, il che ne fa una giornata intera e appagante — pianifichiamo il percorso così passi il tempo a goderti il paesaggio, non la logistica." },
        { q: "È adatta alla fotografia?", a: "Meravigliosa. Conosciamo le classiche strade dei cipressi e la luce migliore, e inseriamo le soste che rendono la Val d'Orcia così iconica." },
        { q: "Il tour è privato?", a: "Del tutto. È una giornata completamente privata sulla tua Mercedes Classe V con autista dedicato." },
      ],
    },
  },
};

export default function ValdorciaGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="valdorcia-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/val-orcia-private-tour", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={HERO}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
